import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Plus, CheckSquare, Calendar, Filter } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Mock data
const MOCK_ACTION_ITEMS = [
  { id: "ai-001", title: "Send revised pricing to Whole Foods", description: "Update the pricing sheet with new volume discounts", status: "open", due_date: "2025-01-13", priority: "high", opportunity_id: "opp-001", opportunity_name: "Whole Foods Market", assigned_to: "John Doe" },
  { id: "ai-002", title: "Schedule demo with Costco buyer", description: "Set up product demo at their regional office", status: "open", due_date: "2025-01-15", priority: "medium", opportunity_id: "opp-002", opportunity_name: "Costco Wholesale", assigned_to: "Jane Smith" },
  { id: "ai-003", title: "Follow up on Target sample request", description: "Send samples for regional test", status: "open", due_date: "2025-01-14", priority: "high", opportunity_id: "opp-003", opportunity_name: "Target Corporation", assigned_to: "John Doe" },
  { id: "ai-004", title: "Prepare Q1 forecast presentation", description: "Create sales forecast deck for leadership review", status: "open", due_date: "2025-01-20", priority: "low", opportunity_id: null, opportunity_name: null, assigned_to: "John Doe" },
  { id: "ai-005", title: "Update CRM with meeting notes", description: "Document yesterday's call outcomes", status: "completed", due_date: "2025-01-10", priority: "medium", opportunity_id: "opp-001", opportunity_name: "Whole Foods Market", assigned_to: "Jane Smith", completed_at: "2025-01-10" },
  { id: "ai-006", title: "Send contract to legal", description: "Get legal review on Sysco agreement", status: "completed", due_date: "2025-01-08", priority: "high", opportunity_id: "opp-005", opportunity_name: "Sysco Foods", assigned_to: "John Doe", completed_at: "2025-01-07" }
];

const ActionItemsPage = ({ navigateToOpportunity }) => {
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newActionItem, setNewActionItem] = useState({
    title: "",
    description: "",
    due_date: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchActionItems();
  }, []);

  const fetchActionItems = async () => {
    try {
      const response = await axios.get(`${API}/crm/action-items`);
      setActionItems(response.data);
    } catch (error) {
      console.error('Failed to fetch action items', error);
      setActionItems(MOCK_ACTION_ITEMS);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActionItem = async () => {
    try {
      await axios.post(`${API}/crm/action-items`, newActionItem);
      toast.success("Action item created");
      setShowCreateModal(false);
      setNewActionItem({ title: "", description: "", due_date: "", priority: "medium" });
      fetchActionItems();
    } catch (error) {
      console.error('Failed to create action item', error);
      toast.error("Failed to create action item");
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      const newStatus = item.status === 'completed' ? 'open' : 'completed';
      await axios.put(`${API}/crm/action-items/${item.id}`, { status: newStatus });
      toast.success(newStatus === 'completed' ? "Marked complete" : "Reopened");
      fetchActionItems();
    } catch (error) {
      console.error('Failed to update action item', error);
      // Update locally for demo
      setActionItems(prev => prev.map(ai => 
        ai.id === item.id ? { ...ai, status: item.status === 'completed' ? 'open' : 'completed' } : ai
      ));
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && statusFilter !== 'completed';
  };

  const filteredItems = actionItems.filter(item => {
    const matchesSearch = searchQuery === "" || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || item.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Sort: overdue first, then by due date
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    const aOverdue = isOverdue(a.due_date);
    const bOverdue = isOverdue(b.due_date);
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;
    return new Date(a.due_date) - new Date(b.due_date);
  });

  // Stats
  const openItems = actionItems.filter(i => i.status === 'open').length;
  const overdueItems = actionItems.filter(i => i.status === 'open' && isOverdue(i.due_date)).length;
  const completedItems = actionItems.filter(i => i.status === 'completed').length;

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Action Items</h1>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CheckSquare className="w-6 h-6" />
          Action Items
        </h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Action Item
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Open</div>
            <div className="text-2xl font-bold">{openItems}</div>
          </CardContent>
        </Card>
        <Card className={overdueItems > 0 ? "border-red-200 bg-red-50" : ""}>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Overdue</div>
            <div className={`text-2xl font-bold ${overdueItems > 0 ? 'text-red-600' : ''}`}>{overdueItems}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Completed</div>
            <div className="text-2xl font-bold text-green-600">{completedItems}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search action items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Action Items List */}
      <div className="space-y-3">
        {sortedItems.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              No action items found
            </CardContent>
          </Card>
        ) : (
          sortedItems.map(item => (
            <Card 
              key={item.id} 
              className={`${item.status === 'completed' ? 'opacity-60' : ''} ${isOverdue(item.due_date) && item.status === 'open' ? 'border-red-300 bg-red-50' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Checkbox
                    checked={item.status === 'completed'}
                    onCheckedChange={() => handleToggleComplete(item)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-semibold ${item.status === 'completed' ? 'line-through text-gray-400' : ''}`}>
                        {item.title}
                      </h3>
                      <Badge className={getPriorityColor(item.priority)}>{item.priority}</Badge>
                      {isOverdue(item.due_date) && item.status === 'open' && (
                        <Badge className="bg-red-500 text-white">Overdue</Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                    )}
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Due: {new Date(item.due_date).toLocaleDateString()}
                      </span>
                      {item.opportunity_name && (
                        <span 
                          className="cursor-pointer hover:text-indigo-600"
                          onClick={() => navigateToOpportunity && navigateToOpportunity(item.opportunity_id)}
                        >
                          🎯 {item.opportunity_name}
                        </span>
                      )}
                      {item.assigned_to && <span>👤 {item.assigned_to}</span>}
                      {item.completed_at && (
                        <span className="text-green-600">✓ Completed {new Date(item.completed_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Action Item Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Action Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newActionItem.title}
                onChange={(e) => setNewActionItem({ ...newActionItem, title: e.target.value })}
                placeholder="What needs to be done?"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newActionItem.description}
                onChange={(e) => setNewActionItem({ ...newActionItem, description: e.target.value })}
                placeholder="Add details..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={newActionItem.due_date}
                  onChange={(e) => setNewActionItem({ ...newActionItem, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={newActionItem.priority} 
                  onValueChange={(val) => setNewActionItem({ ...newActionItem, priority: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateActionItem} disabled={!newActionItem.title || !newActionItem.due_date}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionItemsPage;
